/**
 * CaptionsSessionsListPageV2 — ports the prototype's
 * `.screen-captions-generate` (cms-redesign-mockup.html ~9058+) onto
 * the existing sessions-list + bulk-caption flow.
 *
 * Operator-facing "kick off English caption generation for a batch
 * of sessions" view. Built on top of /admin/sessions with its lean
 * `captions: [{language_code, status, source}]` summary per row.
 *
 * Surface:
 *   • PageHeader (eyebrow Captions, title 'Generate Captions', sub).
 *   • Toolbar: three ChipFilter pills (hide already-captioned,
 *     include inactive, show skip-captions) + an in-page search box
 *     (by session name or #id) for finding a session to generate.
 *   • Bulk-action ribbon when selection > 0: navy ribbon with cost
 *     estimate + Generate / Translate target select / Re-apply vocab /
 *     Clear.
 *   • LibTable: Select / Session / Tracks (status pill cluster) /
 *     Actions (open in Sessions edit).
 *   • Footer pager: client-side; the sessions endpoint isn't paginated.
 *
 * Reuses every helper + action from v1 verbatim (humanizeBulkSkip-
 * Reasons, GENERATION_LANGUAGE, TRANSLATE_TARGETS, etc.) — the page
 * shell + chrome is what changes here.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { fetchAllSessions } from '../../../actions/sessionActions';
import {
  bulkRegenerateCaptions,
  bulkReapplyVocabulary,
  bulkRecueCaptions,
  bulkTranslateCaptions,
} from '../../../actions/captionsActions';
import {
  PageHeader,
  Button,
  ChipFilter,
  LibTable,
  cn,
} from '../../redesign';
import { Select } from '../../redesign/edit';
import { CapStatus, LangChip, SourceTag } from '../../Captions/v2/captionsPrimitives';

/* ─── Constants from v1 (kept verbatim — see CaptionsSessionsListPage.js) ── */

const GENERATION_LANGUAGE = 'en';

const TRANSLATE_TARGETS = [
  { code: 'es',    label: 'Spanish' },
  { code: 'pt-br', label: 'Portuguese (BR)' },
  { code: 'fr',    label: 'French' },
  { code: 'de',    label: 'German' },
  { code: 'it',    label: 'Italian' },
];

const BULK_SKIP_REASON_LABELS = {
  not_found:            'session not found / deleted',
  no_audio:             'missing audio URL',
  no_approved_source:   'no approved English source',
  not_pending_review:   'not in pending review',
  no_captions:          'no caption rows to update',
  no_changes:           "vocabulary didn't match anything",
  skip_captions_enabled: "marked 'skip caption generation'",
};

function humanizeBulkSkipReasons(details) {
  const reasons = (details ?? []).filter((d) => d?.status === 'skipped' && d?.reason).map((d) => d.reason);
  const unique = [...new Set(reasons)];
  if (unique.length === 0) return 'see browser console for per-session details';
  return unique.map((r) => BULK_SKIP_REASON_LABELS[r] || r).join(', ');
}

const PER_PAGE_OPTIONS = [25, 50, 100, 200];

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function CaptionsSessionsListPageV2() {
  const dispatch = useDispatch();
  const sessions = useSelector((s) => s.sessions.all_sessions);
  const isLoading = useSelector((s) => s.sessions.isLoading);

  const [hideAlreadyCaptioned, setHideAlreadyCaptioned] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showSkipCaptions, setShowSkipCaptions] = useState(false);
  const [translateTarget, setTranslateTarget] = useState(TRANSLATE_TARGETS[0].code);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [selected, setSelected] = useState(() => new Set());
  const [dispatching, setDispatching] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchAllSessions());
  }, [dispatch]);

  // The full session list is loaded client-side, so search filters in
  // memory alongside the three toggles — no debounce / refetch needed.
  // Matches by session name (substring) or session id, so operators can
  // find a session to generate without scrolling (Monday 12253813937).
  const searchQuery = search.trim().toLowerCase();
  const matchesSearch = useCallback(
    (s) => {
      if (!searchQuery) return true;
      const name = (s.session_name || '').toLowerCase();
      // Accept the leading "#" the placeholder invites (e.g. "#1608") by
      // stripping it before comparing against the numeric id.
      const idStr = String(s.session_id ?? '');
      const idQuery = searchQuery.replace(/^#/, '');
      return name.includes(searchQuery) || (idQuery !== '' && idStr.includes(idQuery));
    },
    [searchQuery],
  );
  const passesChips = useCallback(
    (s) => {
      const hasEnglishTrack =
        Array.isArray(s.captions) &&
        s.captions.some((c) => c?.language_code === GENERATION_LANGUAGE);
      if (hideAlreadyCaptioned && hasEnglishTrack) return false;
      if (!includeInactive && !s.session_is_active) return false;
      if (!showSkipCaptions && s.session_skip_captions) return false;
      return true;
    },
    [hideAlreadyCaptioned, includeInactive, showSkipCaptions],
  );

  const filtered = useMemo(() => {
    if (!Array.isArray(sessions)) return [];
    return sessions.filter((s) => passesChips(s) && matchesSearch(s));
  }, [sessions, passesChips, matchesSearch]);

  // Sessions that match the search but are excluded ONLY by the active
  // chip filters — lets the empty state explain that a filter is hiding
  // matches instead of implying the search found nothing (Bugbot #434).
  const hiddenByChipsCount = useMemo(() => {
    if (!searchQuery || !Array.isArray(sessions)) return 0;
    return sessions.filter((s) => matchesSearch(s) && !passesChips(s)).length;
  }, [sessions, searchQuery, matchesSearch, passesChips]);

  // Prune selections that have fallen out of the filtered set. Reads
  // current `selected` via the functional setSelected callback so the
  // effect doesn't need `selected` in its deps (which would loop on
  // every selection toggle). Matches the v1 intent without needing
  // an eslint-disable.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(filtered.map((s) => s.session_id));
      let mutated = false;
      const next = new Set(prev);
      for (const id of next) {
        if (!visibleIds.has(id)) {
          next.delete(id);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [filtered]);

  const paged = useMemo(
    () => filtered.slice(page * perPage, (page + 1) * perPage),
    [filtered, page, perPage],
  );

  const selectedRows = useMemo(
    () => filtered.filter((s) => selected.has(s.session_id)),
    [filtered, selected],
  );

  const costEstimate = useMemo(() => {
    const totalSec = selectedRows.reduce(
      (acc, s) => acc + (Number(s.session_length_in_sec) || 0),
      0,
    );
    return { count: selectedRows.length, minutes: totalSec / 60 };
  }, [selectedRows]);

  const translatableSelectedRows = useMemo(
    () =>
      selectedRows.filter(
        (s) =>
          Array.isArray(s.captions) &&
          s.captions.some(
            (c) =>
              c?.language_code === GENERATION_LANGUAGE &&
              (c.status === 'human_reviewed' || c.status === 'human_edited'),
          ),
      ),
    [selectedRows],
  );

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      paged.forEach((s) => {
        if (checked) next.add(s.session_id);
        else next.delete(s.session_id);
      });
      return next;
    });
  };
  const toggleRow = (sessionId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };
  // Add every filtered (non-paged) session to the selection — lets
  // the operator dispatch generation/translation across the entire
  // matching set, not just the visible page. The sessions endpoint
  // returns the full list client-side, so no extra fetch is needed;
  // we just walk `filtered` and union into the existing selection.
  const selectAllMatching = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((s) => next.add(s.session_id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const refreshAfterBulkDispatch = () => {
    dispatch(fetchAllSessions());
    setPage(0);
  };

  const onBulkDispatch = async () => {
    if (selectedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: `Generate captions for ${selectedRows.length} sessions?`,
      html: `~${costEstimate.minutes.toFixed(1)} min of audio will be sent to AssemblyAI.<br/>
             Each session will appear in the Review Queue once transcription finishes.`,
      showCancelButton: true,
      confirmButtonText: 'Generate',
    });
    if (!isConfirmed) return;
    setDispatching(true);
    try {
      const ids = selectedRows.map((s) => s.session_id);
      const result = await dispatch(
        bulkRegenerateCaptions(ids, { language: GENERATION_LANGUAGE, provider: 'assemblyai' }),
      );
      clearSelection();
      if ((result?.queued ?? 0) > 0) refreshAfterBulkDispatch();
      Swal.fire({
        icon: (result?.skipped ?? 0) === 0 ? 'success' : 'warning',
        title: `Queued ${result?.queued ?? 0} session${(result?.queued ?? 0) === 1 ? '' : 's'}`,
        text:
          (result?.skipped ?? 0) === 0
            ? "Each session now shows a grey 'Queued' chip in Tracks, escalating to blue 'Processing' as workers pick them up, then amber 'Pending review' when transcription finishes."
            : `${result.skipped} skipped (${humanizeBulkSkipReasons(result?.details)}).`,
      });
    } finally {
      setDispatching(false);
    }
  };

  const onBulkTranslate = async () => {
    const targetMeta = TRANSLATE_TARGETS.find((t) => t.code === translateTarget);
    if (translatableSelectedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: `Translate ${translatableSelectedRows.length} session${translatableSelectedRows.length === 1 ? '' : 's'} to ${targetMeta?.label ?? translateTarget}?`,
      html:
        translatableSelectedRows.length === selectedRows.length
          ? 'Every selected session has an approved English track. The translation will appear in the Review Queue when each job completes.'
          : `Only ${translatableSelectedRows.length} of ${selectedRows.length} selected sessions have an approved English track. The rest need EN approval first and will be skipped — translate them later.`,
      showCancelButton: true,
      confirmButtonText: 'Translate',
    });
    if (!isConfirmed) return;
    setDispatching(true);
    try {
      const ids = translatableSelectedRows.map((s) => s.session_id);
      const result = await dispatch(bulkTranslateCaptions(ids, translateTarget));
      clearSelection();
      if ((result?.queued ?? 0) > 0) refreshAfterBulkDispatch();
      Swal.fire({
        icon: (result?.skipped ?? 0) === 0 ? 'success' : 'warning',
        title: `Queued ${result?.queued ?? 0} translation${(result?.queued ?? 0) === 1 ? '' : 's'}`,
        text:
          (result?.skipped ?? 0) === 0
            ? `Each ${targetMeta?.label ?? translateTarget} track will appear in the Review Queue as auto_pending_review when the job completes.`
            : `${result.skipped} skipped (${humanizeBulkSkipReasons(result?.details)}).`,
      });
    } finally {
      setDispatching(false);
    }
  };

  const onBulkRecue = async () => {
    if (selectedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: `Re-cue ${selectedRows.length} session${selectedRows.length === 1 ? '' : 's'}?`,
      text:
        'Rebuilds caption cues from the stored words using the latest segmentation. ' +
        'No new audio transcription. Already-reviewed / edited captions are skipped.',
      showCancelButton: true,
      confirmButtonText: 'Re-cue',
    });
    if (!isConfirmed) return;
    setDispatching(true);
    try {
      const ids = selectedRows.map((s) => s.session_id);
      const result = await dispatch(bulkRecueCaptions(ids));
      clearSelection();
      if ((result?.queued ?? 0) > 0) refreshAfterBulkDispatch();
      Swal.fire({
        icon: (result?.queued ?? 0) > 0 ? 'success' : 'info',
        title: `Queued ${result?.queued ?? 0} re-cue job${(result?.queued ?? 0) === 1 ? '' : 's'}`,
        text:
          (result?.queued ?? 0) === 0
            ? 'Nothing queued — the selected sessions have no not-yet-reviewed captions with a saved word track.'
            : (result?.skipped ?? 0) === 0
              ? 'Captions will be rebuilt from the stored words and reappear when the jobs complete.'
              : `${result.skipped} session${result.skipped === 1 ? '' : 's'} skipped (${humanizeBulkSkipReasons(result?.details)}).`,
      });
    } finally {
      setDispatching(false);
    }
  };

  const onBulkReapplyVocab = async () => {
    if (selectedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: `Re-apply vocabulary to ${selectedRows.length} session${selectedRows.length === 1 ? '' : 's'}?`,
      text:
        'Re-runs author aliases + brand-term capitalization on existing VTTs. ' +
        'No new audio transcription. Approval status is preserved.',
      showCancelButton: true,
      confirmButtonText: 'Re-apply',
    });
    if (!isConfirmed) return;
    setDispatching(true);
    try {
      const ids = selectedRows.map((s) => s.session_id);
      const result = await dispatch(bulkReapplyVocabulary(ids));
      clearSelection();
      if ((result?.updated ?? 0) > 0) refreshAfterBulkDispatch();
      Swal.fire({
        icon: (result?.updated ?? 0) > 0 ? 'success' : 'info',
        title: `Updated ${result?.updated ?? 0} session${(result?.updated ?? 0) === 1 ? '' : 's'}`,
        text:
          (result?.updated ?? 0) === 0
            ? "Nothing changed — none of the selected sessions had captions whose text the current vocabulary would alter."
            : (result?.skipped ?? 0) === 0
              ? 'Captions updated in place. Approval status preserved.'
              : `${result.skipped} session${result.skipped === 1 ? '' : 's'} skipped (${humanizeBulkSkipReasons(result?.details)}).`,
      });
    } finally {
      setDispatching(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const hasMore = page + 1 < pageCount;
  const allOnPageSelected =
    paged.length > 0 && paged.every((s) => selected.has(s.session_id));
  const someOnPageSelected =
    paged.some((s) => selected.has(s.session_id)) && !allOnPageSelected;

  const totalLabel = `${filtered.length.toLocaleString()} matching · ${(sessions?.length ?? 0).toLocaleString()} total`;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Captions"
        title="Generate Captions"
        total={totalLabel}
      />

      {/* Filter chips — same primitive as the rest of the V2 pages.
       *  Each chip is an independent boolean toggle (not mutually
       *  exclusive), so ChipFilter is the right fit rather than a
       *  SegmentedControl. The 'Hide already-captioned' chip starts
       *  active because the common workflow is to skip rows that
       *  already have an EN track; the other two are off by default.
       *
       *  Far-right: 'Select all N matching' button so operators can
       *  bulk-select beyond the current page (the per-row checkboxes
       *  + page-header checkbox only ever touch the visible page).
       *  Hidden when no rows match or when every match is already
       *  selected — there's nothing useful to do. */}
      <div
        className="flex items-center"
        style={{ padding: '0 28px 16px', gap: 8, flexWrap: 'wrap' }}
      >
        <ChipFilter
          active={hideAlreadyCaptioned}
          onClick={() => { setHideAlreadyCaptioned((v) => !v); setPage(0); }}
          title="Hides sessions that already have any EN caption row, regardless of status."
        >
          Hide already-captioned
        </ChipFilter>
        <ChipFilter
          active={includeInactive}
          onClick={() => { setIncludeInactive((v) => !v); setPage(0); }}
          title="Inactive sessions usually aren't worth caption budget — they aren't live to users."
        >
          Include inactive sessions
        </ChipFilter>
        <ChipFilter
          active={showSkipCaptions}
          onClick={() => { setShowSkipCaptions((v) => !v); setPage(0); }}
          title="Surfaces rows the operator has marked as no-captions so they can be reviewed or un-flagged."
        >
          Show silent / no-caption sessions
        </ChipFilter>
        <div className="flex items-center" style={{ marginLeft: 'auto', gap: 8 }}>
          {/* In-page search — filters the client-side list by session
           *  name or #id (Monday 12253813937). Typing jumps back to the
           *  first page so matches aren't hidden further in. */}
          <input
            type="search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by session name or #ID…"
            aria-label="Search sessions by name or ID"
            className="bg-surface border border-sand text-navy placeholder:text-text-muted"
            style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 240 }}
          />
          {filtered.length > 0 && selectedRows.length < filtered.length && (
            <Button
              variant="default"
              size="sm"
              onClick={selectAllMatching}
              title="Add every session matching the current filters to the selection — including rows on other pages."
            >
              Select all {filtered.length.toLocaleString()} matching
            </Button>
          )}
        </div>
      </div>

      {/* Bulk-action ribbon — visible when selection > 0. */}
      {selectedRows.length > 0 && (
        <div
          className="flex items-center bg-cream-dark border border-sand text-navy"
          style={{
            margin: '0 28px 16px',
            padding: '10px 16px',
            borderRadius: 8,
            gap: 12,
            flexWrap: 'wrap',
            boxShadow: '0 2px 6px rgb(var(--color-navy) / 0.12)',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            <strong>{costEstimate.count}</strong> selected
          </span>
          <span className="font-mono" style={{ fontSize: 11.5, opacity: 0.75 }}>
            ~{costEstimate.minutes.toFixed(1)} min
            {translatableSelectedRows.length > 0 && translatableSelectedRows.length < selectedRows.length && (
              <>
                {' · '}
                <strong>{translatableSelectedRows.length}</strong> with approved EN
              </>
            )}
          </span>
          <div
            className="flex items-center"
            style={{ marginLeft: 'auto', gap: 8, flexWrap: 'wrap' }}
          >
            {/* Translate target language picker — token-native Select
             *  (cream control on the navy ribbon). */}
            <label
              htmlFor="translate-target"
              className="flex items-center text-navy"
              style={{ gap: 6, fontSize: 12.5, whiteSpace: 'nowrap' }}
            >
              Translate to
              <Select
                id="translate-target"
                value={translateTarget}
                onChange={(e) => setTranslateTarget(e.target.value)}
                style={{ minWidth: 130, width: 'auto' }}
              >
                {TRANSLATE_TARGETS.map((t) => (
                  <option key={t.code} value={t.code}>{t.label}</option>
                ))}
              </Select>
            </label>
            <Button
              variant="default"
              onClick={onBulkTranslate}
              disabled={dispatching || translatableSelectedRows.length === 0}
            >
              Translate {translatableSelectedRows.length}
            </Button>
            <Button
              variant="default"
              onClick={onBulkReapplyVocab}
              disabled={dispatching}
            >
              Re-apply vocab
            </Button>
            <Button
              variant="default"
              onClick={onBulkRecue}
              disabled={dispatching}
              title="Rebuild caption cues from the stored words (latest segmentation) — no re-transcription"
            >
              Re-cue
            </Button>
            <Button variant="ghost" onClick={clearSelection}>
              Clear
            </Button>
            <Button variant="primary" onClick={onBulkDispatch} disabled={dispatching}>
              Generate {selectedRows.length}
            </Button>
          </div>
        </div>
      )}

      {isLoading && !sessions ? (
        <div className="flex items-center justify-center text-text-muted" style={{ height: 256 }}>
          Loading sessions…
        </div>
      ) : paged.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          {hiddenByChipsCount > 0 ? (
            <>
              No <em>visible</em> sessions match — {hiddenByChipsCount.toLocaleString()}{' '}
              {hiddenByChipsCount === 1 ? 'session is' : 'sessions are'} hidden by the
              active filters above. Toggle a filter chip to see {hiddenByChipsCount === 1 ? 'it' : 'them'}.
            </>
          ) : (
            'No sessions match the current filters.'
          )}
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              <col style={{ width: 40 }} />
              <col style={{ width: 70 }} />
              <col />
              <col style={{ width: '40%' }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th aria-label="Select" style={{ paddingLeft: 16 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = someOnPageSelected; }}
                    onChange={(e) => toggleAllOnPage(e.target.checked)}
                  />
                </LibTable.Th>
                <LibTable.Th>ID</LibTable.Th>
                <LibTable.Th>Session</LibTable.Th>
                <LibTable.Th>Tracks</LibTable.Th>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {paged.map((s) => {
                const isSelected = selected.has(s.session_id);
                const inactive = !s.session_is_active;
                return (
                  <LibTable.Row
                    key={s.session_id}
                    inactive={inactive}
                  >
                    <td
                      className="border-b border-line"
                      style={{ paddingLeft: 16, paddingTop: 14, paddingBottom: 14, paddingRight: 8 }}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select session ${s.session_id}`}
                        checked={isSelected}
                        onChange={() => toggleRow(s.session_id)}
                      />
                    </td>
                    <LibTable.Td>
                      <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
                        #{s.session_id}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td>
                      <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
                        <span
                          className={cn(
                            'font-semibold truncate',
                            inactive ? 'text-text-light italic' : 'text-navy',
                          )}
                          style={{ fontSize: 13.5 }}
                          title={s.session_name}
                        >
                          {s.session_name || 'Untitled'}
                        </span>
                        {s.session_skip_captions && (
                          <span className="text-text-muted italic" style={{ fontSize: 11 }}>
                            Marked as skip-captions
                          </span>
                        )}
                      </div>
                    </LibTable.Td>
                    <LibTable.Td>
                      <span className="inline-flex flex-wrap" style={{ gap: 6 }}>
                        {(s.captions ?? []).length === 0 ? (
                          <span className="text-text-muted italic" style={{ fontSize: 12 }}>
                            No captions yet
                          </span>
                        ) : (
                          s.captions.map((c, i) => (
                            <span
                              key={`${c.language_code}-${c.source ?? i}`}
                              className="inline-flex items-center"
                              style={{ gap: 4 }}
                              title={[
                                c.recued_at
                                  ? `Re-cued ${new Date(c.recued_at).toLocaleString()}`
                                  : null,
                                c.last_error || null,
                              ]
                                .filter(Boolean)
                                .join('\n') || undefined}
                            >
                              <LangChip code={c.language_code} status={c.status} />
                              <CapStatus status={c.status} />
                              {c.source && <SourceTag source={c.source} />}
                              {/* Re-cue failures don't flip status (the old
                                  VTT is still valid + served), so without
                                  this marker they'd be invisible here. */}
                              {c.last_error && c.status !== 'failed' && (
                                <span
                                  aria-label="Re-cue failed"
                                  style={{ color: '#C4715B', fontSize: 12, lineHeight: 1 }}
                                >
                                  ⚠
                                </span>
                              )}
                            </span>
                          ))
                        )}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td style={{ textAlign: 'right' }}>
                      <Link to={`/sessions/edit/${s.session_id}`} className="no-underline">
                        <Button variant="default" size="sm">
                          Open
                        </Button>
                      </Link>
                    </LibTable.Td>
                  </LibTable.Row>
                );
              })}
            </tbody>
          </LibTable>

          {/* Pager (client-side; the sessions endpoint is non-paginated). */}
          <div
            className="flex items-center justify-between"
            style={{ padding: '16px 4px 0', gap: 12, flexWrap: 'wrap' }}
          >
            <div className="text-text-muted font-mono" style={{ fontSize: 12 }}>
              Page <strong className="text-navy">{page + 1}</strong> of{' '}
              <strong className="text-navy">{pageCount}</strong>
              {' · '}
              {filtered.length.toLocaleString()} matching
            </div>
            <div className="flex items-center" style={{ gap: 8 }}>
              <label className="text-text-muted font-mono" style={{ fontSize: 12 }}>
                Per page
              </label>
              <select
                value={perPage}
                onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                className="bg-surface border border-sand text-navy"
                style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  fontSize: 12.5,
                  fontFamily: 'inherit',
                }}
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <Button variant="default" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                ‹ Prev
              </Button>
              <Button variant="default" size="sm" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
                Next ›
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
